import * as React from 'react'
import { Copy, Check } from 'lucide-react'
import { toast } from 'sonner'
import { cn, copyText } from '../lib/utils'
import { uiText } from '../i18n'

const TEXT = {
  ru: { copy: 'Скопировать', copied: 'Скопировано', failed: 'Не удалось скопировать' },
  en: { copy: 'Copy', copied: 'Copied', failed: 'Failed to copy' },
}

/**
 * CopyButton — кнопка «Скопировать» с состоянием «Скопировано». Единственное место,
 * где живёт логика копирования: её берут и CopyField, и любые карточки с текстом,
 * который удобно скопировать. Копирует через copyText (фолбэк для не-secure контекста),
 * показывает тост, а подпись на 2 сек. меняется на «Скопировано» с ✓.
 * `pill` — заливка-капсула (рядом с полем), `ghost` — тихая ссылка с иконкой (в карточке).
 */
export interface CopyButtonProps {
  value: string
  /** подпись в покое; дефолт «Скопировать» */
  label?: string
  /** текст тоста при успехе; дефолт «Скопировано» */
  toastText?: string
  variant?: 'pill' | 'ghost'
  className?: string
}

const STYLES = {
  pill: 'rounded-full bg-foreground/10 px-4 py-2 text-xs font-semibold text-foreground hover:bg-foreground/20',
  ghost: 'text-xs text-muted-foreground hover:text-foreground',
}

export function CopyButton({
  value,
  label,
  toastText,
  variant = 'pill',
  className,
}: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false)
  const t = uiText(TEXT)
  const handleCopy = async () => {
    if (!(await copyText(value))) {
      toast.error(t.failed)
      return
    }
    setCopied(true)
    toast.success(toastText ?? t.copied)
    setTimeout(() => setCopied(false), 2000)
  }
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'inline-flex shrink-0 items-center gap-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        STYLES[variant],
        className,
      )}
    >
      {copied ? (
        <Check className="size-3.5 text-success animate-in zoom-in-50 duration-300" />
      ) : (
        <Copy className="size-3.5" />
      )}
      {copied ? t.copied : (label ?? t.copy)}
    </button>
  )
}
